"use strict";
import { TransactionStatus } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { useAccount } from "hooks/useAccount";
import { useEthersWeb3Provider } from "hooks/useEthersProvider";
import { useEffect, useMemo } from "react";
import { usePendingTransactions } from "state/transactions/hooks";
import { logger } from "utilities/src/logger/logger";
import { useEvent } from "utilities/src/react/hooks";
import { parseAtomicBatchStatus } from "utils/parseAtomicBatchStatus";
function usePendingBatches() {
  const transactions = usePendingTransactions();
  const account = useAccount();
  return useMemo(() => {
    if (account.status === "connected") {
      const connectorId = account.connector.id;
      const shouldAttemptCheck = (tx) => {
        const isBatch = tx.batchInfo !== void 0;
        const batchConnectorId = tx.batchInfo?.connectorId;
        return Boolean(isBatch && batchConnectorId === connectorId);
      };
      return transactions.filter(shouldAttemptCheck);
    }
    return [];
  }, [account.status, account.connector?.id, transactions]);
}
const FAILURE_COUNT_THRESHOLD = 4;
const FAILURE_COUNT_MAP = {};
function finalizeBatch(params) {
  const { transaction, onActivityUpdate, hash, status } = params;
  onActivityUpdate({
    type: "transaction",
    chainId: transaction.batchInfo.chainId,
    update: { ...transaction, status, hash },
    original: transaction
  });
  delete FAILURE_COUNT_MAP[transaction.batchInfo.batchId];
}
export function usePollPendingBatchTransactions(onActivityUpdate) {
  const pendingBatchTransactions = usePendingBatches();
  const walletProvider = useEthersWeb3Provider();
  const pollAllPendingBatches = useEvent(async (provider) => {
    for (const transaction of pendingBatchTransactions) {
      try {
        const raw = await provider.send("wallet_getCallsStatus", [transaction.batchInfo.batchId]);
        const result = parseAtomicBatchStatus(raw, transaction.batchInfo);
        const receipt = result?.receipts?.[0];
        if (result.status >= 200 && result.status < 300) {
          if (!receipt) {
            throw new Error(
              `${transaction.batchInfo.connectorId ?? "wallet"} returned a confirmed batch with no receipt`
            );
          }
          const hash = receipt.transactionHash;
          const updatedStatus = result.atomic === true && result.receipts?.every((item) => item.status === "0x1") ? TransactionStatus.Confirmed : TransactionStatus.Failed;
          finalizeBatch({ transaction, onActivityUpdate, hash, status: updatedStatus });
        }
        if (result.status >= 400) {
          finalizeBatch({
            transaction,
            onActivityUpdate,
            hash: receipt?.transactionHash,
            status: TransactionStatus.Failed
          });
        }
        delete FAILURE_COUNT_MAP[transaction.batchInfo.batchId];
      } catch (error) {
        FAILURE_COUNT_MAP[transaction.batchInfo.batchId] = (FAILURE_COUNT_MAP[transaction.batchInfo.batchId] ?? 0) + 1;
        if (FAILURE_COUNT_MAP[transaction.batchInfo.batchId] === FAILURE_COUNT_THRESHOLD) {
          const connectorId = transaction.batchInfo.connectorId;
          logger.error(error, { tags: { file: "batch.ts", function: "pollAllPendingBatches" }, extra: { connectorId } });
        }
      }
    }
  });
  useEffect(() => {
    const interval = walletProvider && pendingBatchTransactions.length > 0 ? setInterval(() => pollAllPendingBatches(walletProvider), 3e3) : void 0;
    return () => clearInterval(interval);
  }, [pendingBatchTransactions, pollAllPendingBatches, walletProvider]);
}
